import { InferenceRule, InferredRelationshipData } from "../InferenceEngine";
import { KnowledgeGraph, Individual } from "@semantical/modules/knowledge-graph";
import Diagram from "@semantical/modules/diagram/Diagram";

/**
 * ArchiMate ontology URIs
 */
const ARCHIMATE_NS = "http://example.org/archimate/3.1#";
const COMPOSED_OF_URI = `${ARCHIMATE_NS}composedOf`;
const SERVES_URI = `${ARCHIMATE_NS}serves`;
const APPLICATION_COMPONENT_URI = `${ARCHIMATE_NS}ApplicationComponent`;
const APPLICATION_INTERFACE_URI = `${ARCHIMATE_NS}ApplicationInterface`;

/**
 * Composition-Serves inference rule.
 *
 * Implements the pattern: A-composedOf->X-serves->B implies A-serves->B
 *
 * Where:
 * - A is an ApplicationComponent
 * - X is an ApplicationInterface (composed within A)
 * - B is an ApplicationComponent (served by X)
 *
 * This rule infers that if an ApplicationComponent A contains an ApplicationInterface X,
 * and X serves another ApplicationComponent B, then A indirectly serves B.
 */
export class CompositionServesRule implements InferenceRule {
    id = "composition-serves";
    name = "Composition-Serves Rule";
    description = "If A-composedOf->X-serves->B then A-serves->B (for ApplicationComponent and ApplicationInterface)";

    infer(knowledgeGraph: KnowledgeGraph, _diagram: Diagram): InferredRelationshipData[] {
        const inferred: InferredRelationshipData[] = [];

        // Get all relationships
        const relationships = Array.from(knowledgeGraph.relationships.values());

        // Find all "composedOf" relationships
        const composedOfRels = relationships.filter(rel =>
            rel.predicate.uri === COMPOSED_OF_URI
        );

        // Find all "serves" relationships
        const servesRels = relationships.filter(rel =>
            rel.predicate.uri === SERVES_URI
        );

        for (const composedOf of composedOfRels) {
            // A is the source (subject) of composedOf
            const A = composedOf.subject;
            // X is the target (object) of composedOf
            const X = composedOf.object;

            // Check if A is an Individual with ApplicationComponent class
            if (!(A instanceof Individual)) continue;
            if (A.modelClass.uri !== APPLICATION_COMPONENT_URI) continue;

            // Check if X is an Individual with ApplicationInterface class
            if (!(X instanceof Individual)) continue;
            if (X.modelClass.uri !== APPLICATION_INTERFACE_URI) continue;

            // Find all "serves" relationships where X is the source
            const servesFromX = servesRels.filter(rel => rel.subject.uri === X.uri);

            for (const serves of servesFromX) {
                // B is the target of serves
                const B = serves.object;

                // Check if B is an Individual with ApplicationComponent class
                if (!(B instanceof Individual)) continue;
                if (B.modelClass.uri !== APPLICATION_COMPONENT_URI) continue;

                // Check if A-serves->B already exists (don't duplicate)
                const alreadyExists = servesRels.some(
                    rel => rel.subject.uri === A.uri && rel.object.uri === B.uri
                );

                if (!alreadyExists) {
                    // Infer A-serves->B
                    inferred.push({
                        subjectUri: A.uri,
                        predicateUri: SERVES_URI,
                        objectUri: B.uri,
                        ruleId: this.id
                    });
                }
            }
        }

        return inferred;
    }
}

export default CompositionServesRule;
